"use client";

import { type RefObject, useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";

/**
 * Toggleable boolean state that closes when the user:
 *   - clicks/taps outside the element bound to `ref`,
 *   - presses `Escape`, or
 *   - navigates to a different route (pathname or query change).
 *
 * Adapted from the official Sitecore `kit-nextjs-product-listing` example so
 * dropdowns/menus across the codebase share a single dismissal contract.
 *
 * @example
 *   const { isVisible, setIsVisible, ref } = useToggleWithClickOutside<HTMLDivElement>();
 *   <div ref={ref}>...</div>
 */
export function useToggleWithClickOutside<T extends HTMLElement = HTMLElement>(
  initialVisible = false,
): {
  isVisible: boolean;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  ref: RefObject<T | null>;
} {
  const [isVisible, setIsVisible] = useState(initialVisible);
  const ref = useRef<T | null>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsVisible(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsVisible(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isVisible]);

  // Auto-close on route change so navigation doesn't leave a menu open.
  // Adjust during render instead of in an effect — React's recommended pattern
  // for resetting state when a prop/value changes (saves an extra commit).
  const [prevPathname, setPrevPathname] = useState(pathname);
  const [prevSearchParams, setPrevSearchParams] = useState(searchParams);
  if (prevPathname !== pathname || prevSearchParams !== searchParams) {
    setPrevPathname(pathname);
    setPrevSearchParams(searchParams);
    setIsVisible(false);
  }

  return { isVisible, setIsVisible, ref };
}
